'use client'

import { ModalContext } from '@/app/components/modal'
import Avatar from '@/components/avatar'
import Button from '@/components/button'
import ChannelTags from '@/components/carousel/channel-tags'
import UAParserContext from '@/context/ua-parser'
import UserContext from '@/context/user-context'
import { IChannel } from '@giphy/js-types'
import { usePathname, useSearchParams } from 'next/navigation'
import { MouseEvent, ReactNode, useContext, useEffect } from 'react'
import { IProfilePowerUser } from 'types'
import { UserAnalytics } from '../analytics-api'
import ChannelAnalyticsOverview from './analytics'
import ChannelPageHeader from './page-header'

import Search from './search'
import SocialButtons from './social-buttons'
import DerankedAvatarOverlay from './deranked-avatar-overlay'

function getHostname(primary_site: string) {
    try {
        return new URL(primary_site).hostname
    } catch (e) {}
    return primary_site
}

type Props = { channel: IChannel; analytics?: UserAnalytics; display_name: ReactNode }
const ChannelInfo = ({ channel, analytics, display_name }: Props) => {
    const { deviceType } = useContext(UAParserContext)
    const { user } = useContext(UserContext)
    const { setModalType } = useContext(ModalContext)
    const pathname = usePathname()
    const searchParams = useSearchParams()
    const showHireMe = searchParams.get('hire') === 'true'
    const {
        user: { about_bio, primary_site },
    } = channel
    useEffect(() => {
        if (showHireMe && user) {
            setModalType({ name: 'hireMe', artist: channel.user })
        }
    }, [channel.user, setModalType, showHireMe, user])
    const showDashboard = user?.permissions.dashboard_analytics && channel.user.id === user.id

    const derankTagNames = [
        'giphyderankofficialaccounthigh',
        'giphyderankofficialaccountlow',
        'giphyderankpromotionalaccounthigh',
        'giphyderankpromotionalaccountlow',
    ]

    const derankTag = Array.isArray(channel.syncable_tags)
        ? channel.syncable_tags.find((t: { tag: string }) => derankTagNames.includes(t.tag))
        : false

    return (
        <div className="flex flex-col gap-3 px-1.5 py-px md:w-[208px] md:px-0">
            {deviceType === 'desktop' ? (
                user?.is_staff && derankTag ? (
                    <DerankedAvatarOverlay user={channel.user} derankTag={derankTag} size={208} />
                ) : (
                    <Avatar user={channel.user} size={208} />
                )
            ) : (
                <ChannelPageHeader channel={channel} title={display_name} />
            )}
            {analytics && <ChannelAnalyticsOverview analytics={analytics} />}
            {showDashboard && (
                <div className="hidden md:flex md:gap-2">
                    <div className="ss-barchart text-giphyGreen"></div>
                    <a href="/dashboard" className="text-sm font-semibold">
                        Visit your Dashboard
                    </a>
                </div>
            )}
            <div className="flex flex-col gap-2 overflow-hidden">
                {about_bio && (
                    <p className="my-2.5 leading-6 whitespace-pre-wrap md:text-[15px] md:text-giphyLightGrey">
                        {about_bio}
                    </p>
                )}
                {primary_site && (
                    <a
                        href={primary_site}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="overflow-hidden text-sm text-ellipsis text-giphyLightGrey md:text-[15px] md:font-semibold md:text-giphyWhite"
                    >
                        {getHostname(primary_site)}
                    </a>
                )}
                <SocialButtons channel={channel} />
            </div>
            <div className="flex flex-col gap-2">
                <Search channel={channel} user={channel.user} className="hidden md:block" />
                {(channel.user as IProfilePowerUser).is_freelance && (
                    <>
                        <Button
                            className="hidden md:block"
                            // TODO - this opens up a panel with the hire me form
                            href={`/login?next=${encodeURIComponent(`${pathname}?hire=true`)}`}
                            rel="nofollow"
                            onClick={(e: MouseEvent<HTMLButtonElement>) => {
                                if (user) {
                                    e.preventDefault()
                                    e.stopPropagation()
                                    setModalType({ name: 'hireMe', artist: channel.user })
                                }
                            }}
                        >
                            <div className="flex items-center justify-center gap-2">
                                <span className="ss-mail" style={{ height: 19 }} />
                                Hire me
                            </div>
                        </Button>
                    </>
                )}
            </div>
            <ChannelTags channel={channel} className="items-start md:flex md:flex-col md:gap-2 md:space-x-0" />
        </div>
    )
}

export default ChannelInfo
